import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ParticleXBackground from "@/components/ParticleXBackground";
import { useTranslation } from "react-i18next";
import {
  Wrench,
  Search,
  Calendar,
  ArrowLeft,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Sparkles,
  Phone,
  Mail,
  CalendarDays,
  Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOL_CATEGORIES_BASE = [
  { id: "all", nameKey: "rentals.all", color: "bg-gray-500" },
  { id: "scaffolding", nameKey: "rentals.scaffolding", color: "bg-blue-500" },
  { id: "safety", nameKey: "rentals.safety", color: "bg-green-500" },
  { id: "power_tools", nameKey: "rentals.powerTools", color: "bg-orange-500" },
  { id: "hand_tools", nameKey: "rentals.handTools", color: "bg-purple-500" },
  { id: "lifting", nameKey: "rentals.lifting", color: "bg-red-500" },
];

export default function PublicRentals() {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Create translated categories
  const TOOL_CATEGORIES = TOOL_CATEGORIES_BASE.map(cat => ({
    ...cat,
    name: t(cat.nameKey)
  }));
  
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTool, setSelectedTool] = useState(null);
  const [rentalDialogOpen, setRentalDialogOpen] = useState(false);
  const [rentalForm, setRentalForm] = useState({
    start_date: "",
    end_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const res = await axios.get(`${API}/tools/public`);
      setTools(res.data);
    } catch (err) {
      console.error("Error fetching tools:", err);
    } finally {
      setLoading(false);
    }
  };

  const openRentalDialog = (tool) => {
    if (!user) {
      toast.error(t('errors.unauthorized'));
      return;
    }
    setSelectedTool(tool);
    setRentalDialogOpen(true);
  };

  const submitRentalRequest = async () => {
    if (!rentalForm.start_date || !rentalForm.end_date) {
      toast.error(t('errors.requiredField'));
      return;
    }

    try {
      await axios.post(`${API}/tools/${selectedTool.tool_id}/rent`, {
        start_date: rentalForm.start_date,
        end_date: rentalForm.end_date,
        notes: rentalForm.notes
      });
      toast.success(t('success.sent'));
      setRentalDialogOpen(false);
      setRentalForm({ start_date: "", end_date: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const filteredTools = tools.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const availableCount = tools.filter(t => t.available).length;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/95 backdrop-blur border-b border-white/10 pt-[env(safe-area-inset-top,0px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{t('common.back')}</span>
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-['Unbounded'] font-bold text-white">AllStillas {t('rentals.title')}</h1>
                  <p className="text-xs text-[#94A3B8]">{t('rentals.equipment')}</p>
                </div>
              </div>
            </div>

            {!user && (
              <Link to="/login">
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700">
                  {t('auth.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-cyan-600/20 via-blue-500/20 to-indigo-500/20 border border-cyan-500/30 p-8 mb-8">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">{t('rentals.title')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-['Unbounded'] font-bold text-white mb-2">
              {t('rentals.availableEquipment')}
            </h2>
            <p className="text-[#94A3B8] max-w-xl mb-4">
              {t('rentals.searchEquipment')}
            </p>
            <div className="flex items-center gap-4">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <CheckCircle className="w-3 h-3 mr-1" />
                {availableCount} {t('rentals.available')}
              </Badge>
              <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                <Clock className="w-3 h-3 mr-1" />
                {t('webshop.shipping')}
              </Badge>
            </div>
          </div>
          <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl"></div>
        </div>

        {/* Contact Info for non-authenticated users */}
        {!user && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('rentals.rentNow')}?</h3>
                <p className="text-[#94A3B8] mb-4">
                  {t('errors.unauthorized')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="mailto:bogdan@doserne.com" className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
                    <Mail className="w-4 h-4" />
                    bogdan@doserne.com
                  </a>
                  <span className="flex items-center gap-2 text-[#94A3B8]">
                    <Phone className="w-4 h-4" />
                    +47 XXX XX XXX
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('rentals.searchEquipment')}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] h-12"
              data-testid="search-tools-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {TOOL_CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat.id)}
                className={`whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? "bg-cyan-500 text-white hover:bg-cyan-600"
                    : "border-white/20 text-[#94A3B8] hover:border-cyan-500/50 hover:text-white"
                }`}
                data-testid={`tool-category-${cat.id}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Tools Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTools.length === 0 ? (
          <div className="text-center py-20">
            <Wrench className="w-16 h-16 mx-auto text-[#475569] mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('rentals.noEquipment')}</h3>
            <p className="text-[#94A3B8]">{t('common.noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTools.map((tool) => (
              <Card 
                key={tool.tool_id} 
                className="bg-[#0A0A0A] border-white/10 overflow-hidden group hover:border-cyan-500/50 transition-all duration-300"
                data-testid={`tool-card-${tool.tool_id}`}
              >
                <div className="aspect-square bg-black/50 relative overflow-hidden">
                  {tool.photos && tool.photos[0] ? (
                    <img
                      src={tool.photos[0]}
                      alt={tool.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Wrench className="w-20 h-20 text-[#475569]" />
                    </div>
                  )}
                  <Badge 
                    className={`absolute top-3 right-3 ${
                      tool.available 
                        ? "bg-green-500/90" 
                        : "bg-red-500/90"
                    }`}
                  >
                    {tool.available ? (
                      <><CheckCircle className="w-3 h-3 mr-1" /> {t('rentals.available')}</>
                    ) : (
                      <><XCircle className="w-3 h-3 mr-1" /> {t('rentals.rented')}</>
                    )}
                  </Badge>
                  <Badge className={`absolute top-3 left-3 ${
                    TOOL_CATEGORIES.find(c => c.id === tool.category)?.color || 'bg-gray-500'
                  }`}>
                    {TOOL_CATEGORIES.find(c => c.id === tool.category)?.name || tool.category}
                  </Badge>
                </div>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                      {tool.name}
                    </h3>
                    {tool.description && (
                      <p className="text-sm text-[#94A3B8] line-clamp-2 mt-1">{tool.description}</p>
                    )}
                  </div>

                  {/* Pricing */}
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[#94A3B8] text-sm">Per zi:</span>
                      <span className="text-xl font-bold text-cyan-400">{tool.daily_rate} NOK</span>
                    </div>
                    {tool.weekly_rate && (
                      <div className="flex items-center justify-between">
                        <span className="text-[#94A3B8] text-sm">Per săptămână:</span>
                        <span className="text-lg font-semibold text-cyan-300">{tool.weekly_rate} NOK</span>
                      </div>
                    )}
                  </div>

                  {/* Condition */}
                  {tool.condition && (
                    <div className="flex items-center gap-2">
                      <span className="text-[#94A3B8] text-sm">Stare:</span>
                      <Badge variant="outline" className={`
                        ${tool.condition === 'new' ? 'border-green-500/50 text-green-400' : ''}
                        ${tool.condition === 'good' ? 'border-blue-500/50 text-blue-400' : ''}
                        ${tool.condition === 'fair' ? 'border-yellow-500/50 text-yellow-400' : ''}
                      `}>
                        {tool.condition === 'new' ? 'Nou' : tool.condition === 'good' ? 'Bună' : 'Acceptabilă'}
                      </Badge>
                    </div>
                  )}

                  <Button
                    onClick={() => openRentalDialog(tool)}
                    disabled={!tool.available}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`rent-tool-${tool.tool_id}`}
                  >
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {tool.available ? "Request Rental" : "Unavailable"}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Rental Request Dialog */}
      <Dialog open={rentalDialogOpen} onOpenChange={setRentalDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-400" />
              Request Rental
            </DialogTitle>
          </DialogHeader>
          {selectedTool && (
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <h4 className="text-white font-medium">{selectedTool.name}</h4>
                <p className="text-cyan-400 font-bold mt-1">{selectedTool.daily_rate} NOK / day</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Start Date</label>
                  <Input
                    type="date"
                    value={rentalForm.start_date}
                    onChange={(e) => setRentalForm(prev => ({ ...prev, start_date: e.target.value }))}
                    className="bg-black/50 border-white/10 text-white"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">End Date</label>
                  <Input
                    type="date"
                    value={rentalForm.end_date}
                    onChange={(e) => setRentalForm(prev => ({ ...prev, end_date: e.target.value }))}
                    className="bg-black/50 border-white/10 text-white"
                    min={rentalForm.start_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block">Notes (optional)</label>
                <Input
                  value={rentalForm.notes}
                  onChange={(e) => setRentalForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional details..."
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>

              <Button
                onClick={submitRentalRequest}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700"
              >
                Submit Request
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
              <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
            </div>
            <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
            <div className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
              <span className="text-[#23F7DD] text-[10px]">⚡</span>
              <span className="text-[10px] text-[#94A3B8]">Powered by</span>
              <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
            </div>
          </Link>
          <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
